import {
  Input,
  InputProps,
  DateRangePickerProps,
  Icon,
  Button,
  ButtonDropdown,
  ButtonDropdownProps,
  DateRangePicker,
  Popover,
  StatusIndicator,
  SpaceBetween,
  Alert,
} from "@amzn/awsui-components-console";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  RELATIVE_OPTIONS,
  RELATIVE_RANGE_STRINGS,
  SEARCH_PARAMETERS,
} from "../searchResults/constants";
import { isEqual } from "lodash";
import moment from "moment-timezone";
export interface SearchByLabel {
  buttonText: string;
  buttonTextSmall: string;
  placeholder: string;
  placeholderSmall: string;
}

export interface LineOfBusinessConfig {
  searchByLabels: { [key: string]: SearchByLabel };
  availableSearchParameters: SEARCH_PARAMETERS[];
  routePath: string;
  metricsPrefix: string;
}

export interface SearchParameters {
  searchBy: SEARCH_PARAMETERS;
  searchTerm: string;
  startDate?: number;
  endDate?: number;
}

interface ClaimsSearchBarProps {
  topNavigation: boolean;
  lineOfBusinessConfig: LineOfBusinessConfig;
  onSearch?: (searchParams: SearchParameters) => void;
  onNavigate?: (route: string) => void;
  validateSearchTerm?: (searchTerm: string, searchBy: SEARCH_PARAMETERS) => string | null;
  publishMetrics?: (searchTerm: string, searchBy: SEARCH_PARAMETERS) => void;
  errorMessage?: string | React.ReactNode;
  onClearError?: () => void;
  maxSearchTermLength?: number;
  showSmallScreenSearchBar?: boolean;
  onToggleSmallSearchBar?: (value: boolean) => void;
}

const ClaimsSearchBar = ({
  topNavigation,
  lineOfBusinessConfig,
  onSearch,
  onNavigate,
  validateSearchTerm,
  publishMetrics,
  errorMessage,
  onClearError,
  maxSearchTermLength = 11,
  showSmallScreenSearchBar = false,
  onToggleSmallSearchBar,
}: ClaimsSearchBarProps) => {
  const SEARCH_BY_LABELS = lineOfBusinessConfig.searchByLabels;

  const generateDropdownItems = (useSmallText: boolean): ButtonDropdownProps.Item[] => {
    return lineOfBusinessConfig.availableSearchParameters.map((param) => ({
      id: param,
      text: useSmallText
        ? SEARCH_BY_LABELS[param].buttonTextSmall
        : SEARCH_BY_LABELS[param].buttonText,
    }));
  };

  const DEFAULT_BUTTON_DROPDOWN_ITEMS = generateDropdownItems(false);
  const SMALL_BUTTON_DROPDOWN_ITEMS = generateDropdownItems(true);

  const [searchParams] = useSearchParams();
  const initializedRef = React.useRef(false);

  const [dateRange, setDateRange] = React.useState<DateRangePickerProps.Value | null>(null);
  const [searchDisabled, setSearchDisabled] = React.useState(true);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [searchByItemKey, setSearchByItemKey] = React.useState<SEARCH_PARAMETERS>(
    lineOfBusinessConfig.availableSearchParameters[0]
  );
  const [buttonDropDownItems, setButtonDropDownItems] = React.useState(
    DEFAULT_BUTTON_DROPDOWN_ITEMS
  );
  const [datePickerChanged, setDatePickerChanged] = useState(false);
  const [alertText, setAlertText] = useState<string | React.ReactNode>("");
  /**
   * Hook to create the event listener for matchMedia which allows us to
   * get the media match for a given query, in our case we're interested in
   * min-width media queries to respond to different screen sizes.
   */
  const useMediaQuery = (query: string) => {
    const mediaQuery = useMemo(() => window.matchMedia(query), [query]);
    const [match, setMatch] = useState(mediaQuery.matches);
    useEffect(() => {
      const onChange = () => setMatch(mediaQuery.matches);
      mediaQuery.addEventListener("change", onChange);

      return () => mediaQuery.removeEventListener("change", onChange);
    }, [mediaQuery]);
    return match;
  };

  /**
   * Helper function to return 4 media query breakpoints for different screen sizes.
   */
  const useMediaQueries = () => {
    const xs = useMediaQuery("(min-width: 320px)");
    const sm = useMediaQuery("(min-width: 480px)");
    const md = useMediaQuery("(min-width: 690px)");
    const lg = useMediaQuery("(min-width: 1000px)");

    return { xs, sm, md, lg };
  };
  const screenSizes = useMediaQueries();

  /**
   * Helper function to determine search type from URL parameters
   */
  const getSearchTypeFromParams = (paramValues: Record<string, string | null>) => {
    for (const param of lineOfBusinessConfig.availableSearchParameters) {
      if (paramValues[param]) {
        return param;
      }
    }
    return lineOfBusinessConfig.availableSearchParameters[0];
  };

  /**
   * Helper function to set date range from URL parameters or default
   */
  const initializeDateRange = (startDateParam: string | null, endDateParam: string | null) => {
    if (startDateParam && endDateParam) {
      const timezoneOffset = new Date().getTimezoneOffset() * 60000;
      setDateRange({
        startDate: new Date(Number.parseInt(startDateParam) - timezoneOffset)
          .toISOString()
          .substring(0, 10),
        endDate: new Date(Number.parseInt(endDateParam) - timezoneOffset)
          .toISOString()
          .substring(0, 10),
        type: "absolute",
      });
    } else {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setFullYear(endDate.getFullYear() - 1);
      const timezoneOffset = new Date().getTimezoneOffset() * 60000;
      setDateRange({
        startDate: new Date(startDate.getTime() - timezoneOffset).toISOString().substring(0, 10),
        endDate: new Date(endDate.getTime() - timezoneOffset).toISOString().substring(0, 10),
        type: "absolute",
      });
    }
  };

  /**
   * Initializes search parameters from URL on first render.
   */
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    // Extract all available search parameters dynamically
    const paramValues: Record<string, string | null> = {};
    let searchTerm = "";

    for (const param of lineOfBusinessConfig.availableSearchParameters) {
      const value = searchParams.get(param);
      paramValues[param] = param === SEARCH_PARAMETERS.ALIAS ? value?.toLowerCase() || null : value;
      if (value && !searchTerm) {
        searchTerm = paramValues[param] || "";
      }
    }

    const startDateParam = searchParams.get(SEARCH_PARAMETERS.START_DATE);
    const endDateParam = searchParams.get(SEARCH_PARAMETERS.END_DATE);

    setSearchTerm(searchTerm);

    const searchType = getSearchTypeFromParams(paramValues);
    setSearchByItemKey(searchType);
    filterButtonDropdownItems(searchType, screenSizes);
    initializeDateRange(startDateParam, endDateParam);
  }, []);

  /**
   * Normalizes the date range based on the provided DateRangePickerProps.Value.
   *
   * @param {DateRangePickerProps.Value | null} dateRange - The date range to be normalized.
   * @returns {object} - An object containing the normalized start and end dates.
   */
  const normalizeDateRange = (dateRange: DateRangePickerProps.Value | null) => {
    let normalizedStartDate = 0;
    let normalizedEndDate = 0;
    // If datepicker search value is absolute calculate start and end dates with specific values
    if (dateRange?.type === "absolute" && dateRange?.startDate && dateRange?.endDate) {
      /**
       * Date().getTimezoneOffset() * 60000 is added to match UTC timezone
       * when requesting the data from the API
       */
      normalizedStartDate =
        Date.parse(dateRange.startDate) + new Date().getTimezoneOffset() * 60000;

      normalizedEndDate = Date.parse(dateRange.endDate) + new Date().getTimezoneOffset() * 60000;

      // Get the timestamp at endDate midnight matching user's timezone
      const endDateMidnight = new Date(normalizedEndDate);
      endDateMidnight.setDate(endDateMidnight.getDate() + 1);
      // Subtract 1 ms from 0:00:00 timestamp to get 23:59:59:999 timestamp
      const midnightTimestamp = endDateMidnight.getTime() - 1;
      // Make sure endDate does not exceed present time
      normalizedEndDate = endDateMidnight.getTime() < Date.now() ? midnightTimestamp : Date.now();

      // Setting date on date picker to normalized dates
      setDateRange({
        startDate: new Date(normalizedStartDate - new Date().getTimezoneOffset() * 60000)
          .toISOString()
          .substring(0, 10),
        endDate: new Date(normalizedEndDate - new Date().getTimezoneOffset() * 60000)
          .toISOString()
          .substring(0, 10),
        type: "absolute",
      });
    }
    // If datepicker search value is relative calculate start and end dates with amount and time units
    else if (dateRange?.type === "relative" && dateRange?.unit && dateRange?.amount) {
      normalizedStartDate = moment().subtract(dateRange.amount, dateRange.unit).valueOf();

      normalizedEndDate = moment.now();
    }
    return { normalizedStartDate, normalizedEndDate };
  };

  /**
   * Publishes the search bar metrics using the provided callback.
   */
  const publishSearchMetrics = (searchTerm: string) => {
    if (publishMetrics) {
      publishMetrics(searchTerm, searchByItemKey);
    }
  };

  const validateSearchTermInternal = (searchTerm: string) => {
    if (topNavigation) {
      return true;
    }
    if (!searchTerm) {
      setAlertText("Search term cannot be empty. Please verify and try again.");
      return false;
    }
    if (searchTerm?.length > maxSearchTermLength) {
      setAlertText(
        `Search term cannot be greater than ${maxSearchTermLength} characters. Please verify and try again.`
      );
      return false;
    }

    // Use custom validation if provided
    if (validateSearchTerm) {
      const customError = validateSearchTerm(searchTerm, searchByItemKey);
      if (customError) {
        setAlertText(customError);
        return false;
      }
    }

    setAlertText("");
    return true;
  };
  /**
   * Handles the search functionality when the user performs a search.
   */
  const handleOnSearch = () => {
    let processedSearchTerm = searchTerm;
    const { normalizedStartDate, normalizedEndDate } = normalizeDateRange(dateRange);

    // If the search is by alias, convert the search term to lowercase
    if (searchByItemKey === SEARCH_PARAMETERS.ALIAS) {
      processedSearchTerm = processedSearchTerm.toLowerCase();
      setSearchTerm(processedSearchTerm);
    }

    if (!validateSearchTermInternal(processedSearchTerm)) {
      return;
    }

    const searchParameters: SearchParameters = {
      searchBy: searchByItemKey,
      searchTerm: processedSearchTerm,
    };

    // If the search is by site, add date range to search parameters
    if (searchByItemKey === SEARCH_PARAMETERS.SITE) {
      searchParameters.startDate = normalizedStartDate;
      searchParameters.endDate = normalizedEndDate;
    }

    publishSearchMetrics(processedSearchTerm);

    // If the search is not from the top navigation, use onSearch callback
    if (!topNavigation && onSearch) {
      onSearch(searchParameters);
    } else if (topNavigation && onNavigate) {
      // For top navigation, construct route and use onNavigate callback
      let startDateText = "";
      let endDateText = "";

      if (searchByItemKey === SEARCH_PARAMETERS.SITE) {
        startDateText = `&${SEARCH_PARAMETERS.START_DATE}=${normalizedStartDate}`;
        endDateText = `&${SEARCH_PARAMETERS.END_DATE}=${normalizedEndDate}`;
      }

      const searchRoute =
        lineOfBusinessConfig.routePath +
        `?${searchByItemKey}=${processedSearchTerm}${startDateText}${endDateText}`;

      onNavigate(searchRoute);
    }
  };

  const onKeyDown = (e: string) => {
    if (e === "Enter" && !searchDisabled) {
      handleOnSearch();
    }
  };

  useEffect(() => {
    if (datePickerChanged) {
      handleOnSearch();
      setDatePickerChanged(false);
    }
  }, [datePickerChanged]);

  useEffect(() => {
    setSearchDisabled(!searchTerm);
  }, [dateRange, searchByItemKey, searchTerm]);

  // Handle external error messages
  useEffect(() => {
    if (errorMessage) {
      setAlertText(errorMessage);
    } else {
      setAlertText("");
    }
  }, [errorMessage]);

  const toggleSmallSearchBar = (value: boolean) => {
    onToggleSmallSearchBar?.(value);
  };

  /**
   * Conditions the search term based on the current search parameters.
   *
   * @param {string} searchTerm - The search term to be conditioned.
   * @returns {string} - The conditioned search term.
   */
  const conditionSearchTerm = (searchTerm: string) => {
    // If the search is by site, convert the search term to uppercase
    if (searchByItemKey === SEARCH_PARAMETERS.SITE) {
      return searchTerm.toUpperCase();
    }
    // If the search is by alias, convert the search term to lowercase
    else if (searchByItemKey === SEARCH_PARAMETERS.ALIAS) {
      return searchTerm.toLowerCase();
    }
    // If the search is not by site or alias, return the search term as is
    return searchTerm;
  };

  const renderBarInput = (screenSizes: { [key: string]: boolean }) => {
    const currentSearchConfig = SEARCH_BY_LABELS[searchByItemKey];
    if (!currentSearchConfig) {
      return null;
    }

    const placeholder = !screenSizes?.lg
      ? currentSearchConfig.placeholderSmall
      : currentSearchConfig.placeholder;
    return (
      <div className="input-filter" id="input-filter">
        <Input
          data-testid="search-bar"
          clearAriaLabel="clear search bar"
          onKeyDown={({ detail }: { detail: InputProps.KeyDetail }) => onKeyDown(detail.key)}
          value={searchTerm}
          placeholder={placeholder}
          type="search"
          onChange={({ detail }: { detail: InputProps.ChangeDetail }) =>
            setSearchTerm(conditionSearchTerm(detail.value))
          }
        />
      </div>
    );
  };
  const filterButtonDropdownItems = (filterId: string, screenSizes: { [key: string]: boolean }) => {
    const { sm } = screenSizes;
    const items = !sm ? SMALL_BUTTON_DROPDOWN_ITEMS : DEFAULT_BUTTON_DROPDOWN_ITEMS;
    const filteredItems = items.filter((item: ButtonDropdownProps.Item) => item.id !== filterId);
    if (!isEqual(filteredItems, buttonDropDownItems)) {
      setButtonDropDownItems(filteredItems);
    }
  };
  /**
   * Renders the bar in 3 different states:
   *   - Large Screen size: Normal bar and advanced button
   *   - Medium Screen size:
   *     - Hidden search bar with only search button.
   *     - Normal size search bar with reduced size advanced button.
   */
  const responsiveRenderBar = () => {
    const screenSizes = useMediaQueries();
    filterButtonDropdownItems(searchByItemKey, screenSizes);

    // Safety check to ensure searchByItemKey exists in SEARCH_BY_LABELS
    const currentSearchConfig = SEARCH_BY_LABELS[searchByItemKey];
    if (!currentSearchConfig) {
      console.warn(`Search configuration not found for ${searchByItemKey}, using default`);
      return null;
    }

    const buttonText =
      !screenSizes.sm && topNavigation
        ? currentSearchConfig.buttonTextSmall
        : currentSearchConfig.buttonText;
    if (topNavigation && !screenSizes?.md) {
      if (showSmallScreenSearchBar) {
        return (
          <React.Fragment>
            {renderBarInput(screenSizes)}
            <div className="search-by-button">
              {lineOfBusinessConfig.availableSearchParameters.length === 1 ? (
                <Button data-testid="search-by-button" onClick={handleOnSearch}>
                  {buttonText}
                </Button>
              ) : (
                <ButtonDropdown
                  data-testid="search-by-button"
                  items={buttonDropDownItems}
                  expandableGroups
                  onItemClick={({ detail }: { detail: ButtonDropdownProps.ItemClickDetails }) => {
                    setSearchByItemKey(detail.id as SEARCH_PARAMETERS);
                    filterButtonDropdownItems(detail.id, screenSizes);
                  }}
                  mainAction={{
                    text: buttonText,
                    onClick: handleOnSearch,
                  }}
                ></ButtonDropdown>
              )}
            </div>

            <button
              data-testid="hide-search-bar"
              onClick={() => {
                toggleSmallSearchBar(false);
              }}
              className="transparent-button close-search-button"
              aria-label="Hide Claims Search Bar"
            >
              <Icon
                name="close"
                size="medium"
                variant={topNavigation ? "disabled" : "normal"}
              ></Icon>
            </button>
          </React.Fragment>
        );
      }
      const borderClass = !topNavigation ? "no-border" : "";
      return (
        <div className={`search-icon ${borderClass}`}>
          <button
            className="transparent-button"
            onClick={() => toggleSmallSearchBar(true)}
            aria-label="Show Claims Search Bar"
            data-testid="show-search-bar"
          >
            <Icon
              name="search"
              size="medium"
              variant={topNavigation ? "disabled" : "normal"}
            ></Icon>
          </button>
        </div>
      );
    }

    return (
      <React.Fragment>
        {renderBarInput(screenSizes)}
        <div className="search-by-button">
          {lineOfBusinessConfig.availableSearchParameters.length === 1 ? (
            <Button data-testid="search-by-button" onClick={handleOnSearch}>
              {buttonText}
            </Button>
          ) : (
            <ButtonDropdown
              data-testid="search-by-button"
              items={buttonDropDownItems}
              expandableGroups
              onItemClick={({ detail }: { detail: ButtonDropdownProps.ItemClickDetails }) => {
                setSearchByItemKey(detail.id as SEARCH_PARAMETERS);
                filterButtonDropdownItems(detail.id, screenSizes);
                setSearchTerm("");
              }}
              mainAction={{
                text: buttonText,
                onClick: handleOnSearch,
              }}
            ></ButtonDropdown>
          )}
        </div>
      </React.Fragment>
    );
  };
  const renderDateRangePicker = () => {
    if (topNavigation || searchByItemKey !== SEARCH_PARAMETERS.SITE) {
      return null;
    }
    return (
      <SpaceBetween direction="horizontal" size="xs">
        <DateRangePicker
          data-testid="search-bar-date-picker"
          onChange={({ detail }) => {
            setDateRange(detail.value);
            setDatePickerChanged(true);
          }}
          dateOnly={true}
          value={dateRange as DateRangePickerProps.Value}
          relativeOptions={RELATIVE_OPTIONS}
          i18nStrings={{
            formatRelativeRange: (value: DateRangePickerProps.RelativeValue) => {
              const preloadedRelativeString = value.key ? RELATIVE_RANGE_STRINGS[value.key] : "";
              if (preloadedRelativeString) {
                return preloadedRelativeString;
              }
              return `Past ${value.amount} ${value.unit}${value.amount > 1 ? "s" : ""}`;
            },
            relativeModeTitle: "Relative Date Ranges",
            absoluteModeTitle: "Specific Date Range",
          }}
          isValidRange={(range) => {
            if (!range) {
              return {
                valid: false,
                errorMessage: "Invalid selection. Please select a relative or specific date range.",
              };
            }
            if (range?.type === "relative") {
              if (!range.amount) {
                return {
                  valid: false,
                  errorMessage: "Invalid selection. Please select an amount for your custom range.",
                };
              }
            }
            if (range?.type === "absolute") {
              const [startDateWithoutTime] = range.startDate.split("T");
              const [endDateWithoutTime] = range.endDate.split("T");
              if (!startDateWithoutTime || !endDateWithoutTime) {
                return {
                  valid: false,
                  errorMessage:
                    "The selected date range is incomplete. Select a start and end date for the date range.",
                };
              }
              if (Date.parse(range.startDate) > Date.parse(range.endDate)) {
                return {
                  valid: false,
                  errorMessage:
                    "The selected date range is invalid. The start date must be before the end date.",
                };
              }
              const nextMidnight = new Date();
              nextMidnight.setHours(24, 0, 0, 0);
              const nextMidnightUTC = nextMidnight.getTime();
              if (
                Date.parse(range.startDate) > nextMidnightUTC ||
                Date.parse(range.endDate) > nextMidnightUTC
              ) {
                return {
                  valid: false,
                  errorMessage:
                    "The selected date range is invalid. Dates cannot be in the future.",
                };
              }
            }
            return { valid: true };
          }}
          placeholder="Filter by a date range"
        />
        <Popover
          header="Filter by submitted date"
          content="Filter the search results by selecting a relative date (past month, past year, etc.) or a specific date range."
          triggerType="custom"
          data-testid="claims-search-bar-date-range-popover"
        >
          <StatusIndicator type="info" />
        </Popover>
      </SpaceBetween>
    );
  };

  const renderAlert = () => {
    if (!alertText || topNavigation) return null;
    return (
      <div className="search-bar-alert">
        <Alert
          dismissible={true}
          type="error"
          statusIconAriaLabel={"Error"}
          dismissAriaLabel={"Close Alert"}
          key="search-bar-alert"
          onDismiss={() => {
            setAlertText("");
            if (onClearError) {
              onClearError();
            }
          }}
        >
          {alertText}
        </Alert>
      </div>
    );
  };
  return (
    <React.Fragment>
      <div className={`claims-search-bar ${!topNavigation ? "flex-wrap" : ""}`}>
        {responsiveRenderBar()}
        {renderDateRangePicker()}
      </div>
      {renderAlert()}
    </React.Fragment>
  );
};
export default ClaimsSearchBar;
